// Advanced user tracking with section visibility and engagement metrics

interface SectionView {
  sectionId: string
  sectionTitle: string
  timeInView: number
  percentVisible: number
  firstViewed: number
  lastViewed: number
  enterTime?: number // Track when currently viewing started
  scrollDepth: number
  textPreview?: string
}

interface ScrollData {
  maxDepth: number
  averageSpeed: number
  totalScrollDistance: number
  scrollEvents: number
}

interface EngagementMetrics {
  totalTime: number
  activeTime: number
  idleTime: number
  clickCount: number
  hoverEvents: number
  focusTime: number
}

interface AdvancedUserJourney {
  // Basic tracking
  referrer: string
  landingPage: string
  deviceInfo: {
    type: string
    browser: string
    os: string
    screenResolution: string
    viewport: string
  }

  // Page tracking
  pagesVisited: Array<{
    path: string
    title: string
    timestamp: number
    timeSpent: number
    exitType: 'navigation' | 'back' | 'close' | 'form' | 'unknown'
  }>

  // Section tracking
  sectionsViewed: Map<string, SectionView>

  // Scroll tracking
  scrollData: ScrollData

  // Engagement
  engagement: EngagementMetrics

  // Conversion
  conversionPath: string[]
  conversionButton?: string
  formFieldsCompleted?: string[]

  // Session
  sessionStart: number
  sessionEnd?: number

  // UTM
  utm?: {
    source?: string
    medium?: string
    campaign?: string
    term?: string
    content?: string
  }

  // Google Ads
  gclid?: string

  // Location
  location?: {
    ip?: string
    city?: string
    region?: string
    country?: string
    countryCode?: string
    postal?: string
    latitude?: number
    longitude?: number
    timezone?: string
    isp?: string
  } | null
}

const STORAGE_KEY = 'advanced_journey'
let currentJourney: AdvancedUserJourney | null = null
let sectionObserver: IntersectionObserver | null = null

// Get user location from IP
let locationPromise: Promise<any> | null = null
const LOCATION_CACHE_KEY = 'cached_user_location'
const LOCATION_ATTEMPT_KEY = 'location_attempt_timestamp'
const LOCATION_CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours
const MIN_REQUEST_INTERVAL = 60 * 60 * 1000 // 1 hour minimum between requests

async function getUserLocation() {
  if (typeof window === 'undefined') return null
  
  // Check for cached location first (persists across pages in session)
  const cachedData = sessionStorage.getItem(LOCATION_CACHE_KEY)
  if (cachedData) {
    try {
      return JSON.parse(cachedData)
    } catch {}
  }
  
  // Check if we're rate limited
  const rateLimitExpiry = sessionStorage.getItem('location_rate_limit_expiry')
  if (rateLimitExpiry && Date.now() < parseInt(rateLimitExpiry)) {
    return null // Still in rate limit period
  }
  
  // Only allow one request per session to avoid multiple tabs/pages hitting API
  const requestInProgress = sessionStorage.getItem('location_request_in_progress')
  if (requestInProgress === 'true') {
    return null
  }
  
  // Mark request in progress
  sessionStorage.setItem('location_request_in_progress', 'true')
  
  // Wait 5 seconds before making request (allow page to load first)
  await new Promise(resolve => setTimeout(resolve, 5000))
  
  try {
    // Use XMLHttpRequest to have more control over error handling
    const data = await new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', 'https://ipapi.co/json/', true)
      xhr.setRequestHeader('Accept', 'application/json')
      xhr.timeout = 5000
      
      xhr.onload = function() {
        sessionStorage.removeItem('location_request_in_progress')
        
        if (xhr.status === 200) {
          try {
            const result = JSON.parse(xhr.responseText)
            resolve(result)
          } catch {
            resolve(null)
          }
        } else if (xhr.status === 429) {
          // Set rate limit expiry for 1 hour
          sessionStorage.setItem('location_rate_limit_expiry', (Date.now() + 3600000).toString())
          resolve(null)
        } else {
          resolve(null)
        }
      }
      
      xhr.onerror = function() {
        sessionStorage.removeItem('location_request_in_progress')
        resolve(null)
      }
      
      xhr.ontimeout = function() {
        sessionStorage.removeItem('location_request_in_progress')
        resolve(null)
      }
      
      // Send request
      xhr.send()
    })
    
    if (data) {
      const locationData = {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        countryCode: data.country_code,
        postal: data.postal,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
        isp: data.org
      }
      
      // Cache the successful response for the entire session
      sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(locationData))
      
      return locationData
    }
  } catch (error) {
    // Silent fail
    sessionStorage.removeItem('location_request_in_progress')
  }
  
  return null
}
let scrollTimeout: NodeJS.Timeout | null = null
let lastScrollPosition = 0
let totalScrollDistance = 0
let scrollEvents = 0
let isActive = true
let lastActivityTime = Date.now()
let idleTime = 0

// Initialize advanced tracking
export function initializeAdvancedTracking() {
  if (typeof window === 'undefined') return

  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[User Journey] Initializing advanced tracking...')
  }

  // Check for existing journey
  const stored = sessionStorage.getItem(STORAGE_KEY)
  if (stored) {
    currentJourney = JSON.parse(stored, (key, value) => {
      if (key === 'sectionsViewed') {
        return new Map(value)
      }
      return value
    })
    if (process.env.NODE_ENV === 'development') {
      console.log('[User Journey] Restored existing session')
    }
  }

  if (!currentJourney) {
    currentJourney = {
      referrer: document.referrer || 'Direct',
      landingPage: window.location.pathname,
      deviceInfo: getDeviceInfo(),
      pagesVisited: [{
        path: window.location.pathname,
        title: document.title,
        timestamp: Date.now(),
        timeSpent: 0,
        exitType: 'unknown'
      }],
      sectionsViewed: new Map(),
      scrollData: {
        maxDepth: 0,
        averageSpeed: 0,
        totalScrollDistance: 0,
        scrollEvents: 0
      },
      engagement: {
        totalTime: 0,
        activeTime: 0,
        idleTime: 0,
        clickCount: 0,
        hoverEvents: 0,
        focusTime: 0
      },
      conversionPath: [],
      sessionStart: Date.now(),
      utm: getUTMParams(),
      gclid: getGclid(),
      location: null // Will be populated async
    }
    
    // Get location data asynchronously with better error handling
    // First check if we have cached location data
    const cachedLocation = sessionStorage.getItem(LOCATION_CACHE_KEY)
    if (cachedLocation) {
      try {
        currentJourney.location = JSON.parse(cachedLocation)
      } catch {}
    }
    
    // If no cached location, check existing journey
    if (!currentJourney.location) {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      const existingJourney = stored ? JSON.parse(stored) : null
      if (existingJourney?.location) {
        currentJourney.location = existingJourney.location
      }
    }
    
    // Only fetch if we don't have location data yet
    if (!currentJourney.location) {
      // Fetch location in background without blocking
      locationPromise = getUserLocation()
      if (locationPromise) {
        locationPromise.then(location => {
          if (location && currentJourney) {
            currentJourney.location = location
            saveJourney()
          }
        }).catch(() => {
          // Silently ignore any errors
        })
      }
    }
  }

  // Initialize document height cache
  updateDocumentHeightCache()
  
  // Update cache on resize
  window.addEventListener('resize', updateDocumentHeightCache, { passive: true })

  // Set up observers and listeners
  if (process.env.NODE_ENV === 'development') {
    console.log('[User Journey] Setting up observers...')
  }
  setupSectionObserver()
  setupScrollTracking()
  setupEngagementTracking()
  setupVisibilityTracking()
  if (process.env.NODE_ENV === 'development') {
    console.log('[User Journey] Advanced tracking fully initialized')
  }

  // Save periodically
  setInterval(saveJourney, 5000)
}

// Get device information
function getDeviceInfo() {
  const ua = navigator.userAgent
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua)
  
  // Read layout properties once at initialization
  // These rarely change and don't need real-time updates
  return {
    type: mobile ? 'mobile' : 'desktop',
    browser: getBrowser(ua),
    os: getOS(ua),
    screenResolution: `${screen.width}x${screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  }
}

function getBrowser(ua: string): string {
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edge')) return 'Edge'
  return 'Other'
}

function getOS(ua: string): string {
  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
  return 'Other'
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search)
  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    term: params.get('utm_term') || undefined,
    content: params.get('utm_content') || undefined
  }
}

// Get Google Click ID (GCLID) for offline conversion tracking
function getGclid(): string | undefined {
  const params = new URLSearchParams(window.location.search)
  const gclidFromUrl = params.get('gclid')

  // If GCLID is in URL, store it in sessionStorage
  if (gclidFromUrl) {
    sessionStorage.setItem('gclid', gclidFromUrl)
    sessionStorage.setItem('gclid_timestamp', Date.now().toString())
    return gclidFromUrl
  }

  // Otherwise check sessionStorage (user may have navigated from landing page)
  const storedGclid = sessionStorage.getItem('gclid')
  return storedGclid || undefined
}

// Set up Intersection Observer for sections
function setupSectionObserver() {
  if (!currentJourney || typeof window === 'undefined') return

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0]
  }

  // Cache for section metadata to avoid DOM queries
  const sectionMetadataCache = new WeakMap<Element, { id: string, title: string, textPreview: string }>()

  sectionObserver = new IntersectionObserver((entries) => {
    // Use requestAnimationFrame to batch DOM operations
    requestAnimationFrame(() => {
      // Batch read all DOM properties first
      const metadataToProcess = entries.map(entry => {
        let metadata = sectionMetadataCache.get(entry.target)
        
        // Cache metadata on first encounter
        if (!metadata) {
          const elementId = entry.target.id
          const dataSection = entry.target.getAttribute('data-section')
          const headingText = entry.target.querySelector('h1, h2, h3')?.textContent?.trim()
          
          // Get unique identifier based on DOM position if needed
          const domPath = Array.from(entry.target.classList).join('-') || entry.target.tagName.toLowerCase()
          
          // Use the most specific identifier available, combine multiple to ensure uniqueness
          const sectionId = elementId || 
                           dataSection || 
                           `${domPath}-${headingText?.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '-') || 'section'}`
          
          const sectionTitle = entry.target.getAttribute('data-title') || headingText || sectionId
          
          // Get text preview
          const textContent = entry.target.textContent?.trim() || ''
          const textPreview = textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '')
          
          metadata = { id: sectionId, title: sectionTitle, textPreview }
          sectionMetadataCache.set(entry.target, metadata)
        }
        
        return { entry, metadata }
      })
      
      // Now process all entries with cached metadata
      metadataToProcess.forEach(({ entry, metadata }) => {
        if (!currentJourney || !metadata) return

        let sectionView = currentJourney.sectionsViewed.get(metadata.id)
        
        // Consider a section "in view" only if at least 10% is visible
        const isInView = entry.isIntersecting && entry.intersectionRatio > 0.1
        
        if (isInView) {
          if (!sectionView) {
            sectionView = {
              sectionId: metadata.id,
              sectionTitle: metadata.title,
              timeInView: 0,
              percentVisible: entry.intersectionRatio * 100,
              firstViewed: Date.now(),
              lastViewed: Date.now(),
              enterTime: Date.now(), // Track when section entered view
              scrollDepth: getScrollDepth(),
              textPreview: metadata.textPreview
            }
            currentJourney.sectionsViewed.set(metadata.id, sectionView)
          } else if (!sectionView.enterTime) {
            // Section re-entered view (only if not already tracking)
            sectionView.enterTime = Date.now() // Reset enter time
            sectionView.lastViewed = Date.now()
            sectionView.percentVisible = Math.max(sectionView.percentVisible, entry.intersectionRatio * 100)
        } else {
          // Update visibility percentage if still in view
          sectionView.percentVisible = Math.max(sectionView.percentVisible, entry.intersectionRatio * 100)
        }
        } else if (sectionView && sectionView.enterTime) {
          // Section left view, calculate time since it entered
          const timeSpent = Date.now() - sectionView.enterTime
          sectionView.timeInView += timeSpent
          sectionView.enterTime = undefined // Clear enter time
        }
      })
      
      saveJourney()
    })
  }, options)

  // Observe all major sections
  setTimeout(() => {
    // Priority: data-section attribute (from TrackableSection), then regular sections
    // But avoid duplicates by checking if element has already been observed
    const observedElements = new Set<Element>()
    
    // First observe elements with explicit tracking attributes
    document.querySelectorAll('[data-section], [data-track-section]').forEach(section => {
      if (sectionObserver && !observedElements.has(section)) {
        sectionObserver.observe(section)
        observedElements.add(section)
      }
    })
    
    // Then observe semantic section elements (if not already observed)
    document.querySelectorAll('section').forEach(section => {
      if (sectionObserver && !observedElements.has(section)) {
        sectionObserver.observe(section)
        observedElements.add(section)
      }
    })
    
    // Total sections tracked: observedElements.size
  }, 1000)
}

// Track if we're already processing a scroll event
let scrollRAF: number | null = null

// Set up scroll tracking
function setupScrollTracking() {
  if (typeof window === 'undefined') return

  window.addEventListener('scroll', () => {
    // Cancel any pending animation frame
    if (scrollRAF) {
      cancelAnimationFrame(scrollRAF)
    }

    // Use requestAnimationFrame to batch DOM reads
    scrollRAF = requestAnimationFrame(() => {
      if (!currentJourney) {
        scrollRAF = null
        return
      }
      
      const currentScroll = window.scrollY
      const scrollDiff = Math.abs(currentScroll - lastScrollPosition)
      
      totalScrollDistance += scrollDiff
      scrollEvents++
      
      const scrollDepth = getScrollDepth()
      currentJourney.scrollData.maxDepth = Math.max(currentJourney.scrollData.maxDepth, scrollDepth)
      currentJourney.scrollData.totalScrollDistance = totalScrollDistance
      currentJourney.scrollData.scrollEvents = scrollEvents
      
      lastScrollPosition = currentScroll

      // Debounced save
      if (scrollTimeout) clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(saveJourney, 1000)
      
      scrollRAF = null
    })
  }, { passive: true }) // Use passive listener for better performance
}

// Cache document height to avoid forced reflows
let cachedDocumentHeight = 0
let documentHeightCacheTime = 0
const CACHE_DURATION = 1000 // Cache for 1 second

// Update document height cache in RAF to avoid forced reflow
function updateDocumentHeightCache() {
  requestAnimationFrame(() => {
    cachedDocumentHeight = document.documentElement.scrollHeight
    documentHeightCacheTime = Date.now()
  })
}

// Get current scroll depth percentage
function getScrollDepth(): number {
  const windowHeight = window.innerHeight
  const scrollTop = window.scrollY
  
  // Use cached document height if available and recent
  const now = Date.now()
  if (now - documentHeightCacheTime > CACHE_DURATION || cachedDocumentHeight === 0) {
    // Schedule an update but use stale value for now
    updateDocumentHeightCache()
    // Use a fallback if no cached value exists
    if (cachedDocumentHeight === 0) {
      cachedDocumentHeight = document.documentElement.scrollHeight
    }
  }
  
  if (cachedDocumentHeight <= windowHeight) {
    return 100
  }
  
  return Math.round(((scrollTop + windowHeight) / cachedDocumentHeight) * 100)
}

// Set up engagement tracking
function setupEngagementTracking() {
  if (typeof window === 'undefined') return

  // Click tracking
  document.addEventListener('click', (e) => {
    if (!currentJourney) return
    currentJourney.engagement.clickCount++
    
    // Track conversion path
    const target = e.target as HTMLElement
    const button = target.closest('button, a')
    if (button) {
      const text = button.textContent?.trim() || ''
      if (text) {
        currentJourney.conversionPath.push(text.substring(0, 50))
      }
    }
    
    updateActivity()
  })

  // Hover tracking (sample rate to avoid performance issues)
  let hoverCount = 0
  document.addEventListener('mousemove', () => {
    hoverCount++
    if (hoverCount % 50 === 0 && currentJourney) {
      currentJourney.engagement.hoverEvents++
    }
    updateActivity()
  })

  // Focus tracking
  let focusStart = 0
  window.addEventListener('focus', () => {
    focusStart = Date.now()
    isActive = true
  })

  window.addEventListener('blur', () => {
    if (focusStart && currentJourney) {
      currentJourney.engagement.focusTime += Date.now() - focusStart
    }
    isActive = false
  })
}

// Update activity status
function updateActivity() {
  const now = Date.now()
  if (now - lastActivityTime > 30000) { // 30 seconds idle
    idleTime += now - lastActivityTime
  }
  lastActivityTime = now
}

// Set up page visibility tracking
function setupVisibilityTracking() {
  if (typeof document === 'undefined') return

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Page hidden
      updateCurrentPageTime()
    }
  })

  // Before unload
  window.addEventListener('beforeunload', () => {
    updateCurrentPageTime()
    if (currentJourney) {
      currentJourney.sessionEnd = Date.now()
    }
    saveJourney()
  })
}

// Update time spent on current page
function updateCurrentPageTime() {
  if (!currentJourney) return
  
  const currentPage = currentJourney.pagesVisited[currentJourney.pagesVisited.length - 1]
  if (currentPage) {
    currentPage.timeSpent = Date.now() - currentPage.timestamp
  }

  // Update engagement metrics
  const sessionTime = Date.now() - currentJourney.sessionStart
  currentJourney.engagement.totalTime = sessionTime
  currentJourney.engagement.activeTime = sessionTime - idleTime
  currentJourney.engagement.idleTime = idleTime
}

// Save journey to storage
function saveJourney() {
  if (!currentJourney) return
  
  updateCurrentPageTime()
  
  const toStore = {
    ...currentJourney,
    sectionsViewed: Array.from(currentJourney.sectionsViewed.entries())
  }
  
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
}

// Track page navigation
export function trackPageNavigation(exitType: 'navigation' | 'back' | 'close' | 'form' = 'navigation') {
  if (!currentJourney) return
  
  const currentPage = currentJourney.pagesVisited[currentJourney.pagesVisited.length - 1]
  if (currentPage) {
    currentPage.exitType = exitType
  }
  
  // Add new page
  currentJourney.pagesVisited.push({
    path: window.location.pathname,
    title: document.title,
    timestamp: Date.now(),
    timeSpent: 0,
    exitType: 'unknown'
  })
  
  // Reset section tracking for new page
  currentJourney.sectionsViewed.clear()
  
  // Re-setup observers
  if (sectionObserver) {
    sectionObserver.disconnect()
  }
  setupSectionObserver()
  
  saveJourney()
}

// Track conversion
export function trackAdvancedConversion(buttonName: string, formFields?: string[]) {
  if (!currentJourney) return
  
  currentJourney.conversionButton = buttonName
  currentJourney.formFieldsCompleted = formFields
  currentJourney.sessionEnd = Date.now()
  
  const currentPage = currentJourney.pagesVisited[currentJourney.pagesVisited.length - 1]
  if (currentPage) {
    currentPage.exitType = 'form'
  }
  
  saveJourney()
}

// Get formatted journey data for email
// Finalize any sections still being viewed
function finalizeSectionTimes() {
  if (!currentJourney) return
  
  currentJourney.sectionsViewed.forEach((view, id) => {
    // If section still has an enter time, it means user is still viewing it
    if (view.enterTime) {
      const timeSpent = Date.now() - view.enterTime
      view.timeInView += timeSpent
      view.enterTime = undefined
    }
  })
}

export async function getAdvancedTrackingData() {
  if (!currentJourney) return null
  
  updateCurrentPageTime()
  finalizeSectionTimes() // Finalize any active section times
  
  // Wait for location data if it's still being fetched (one attempt only)
  if (locationPromise && !currentJourney.location) {
    try {
      const location = await Promise.race([
        locationPromise,
        new Promise(resolve => setTimeout(() => resolve(null), 2000)) // 2 second timeout
      ])
      if (location) {
        currentJourney.location = location
      }
    } catch (error) {
      // Silently fail - location is not critical
    }
  }
  
  return {
    ...currentJourney,
    sectionsViewed: Array.from(currentJourney.sectionsViewed.entries()).map(([id, view]) => ({
      ...view,
      formattedTime: formatTime(view.timeInView)
    })),
    formattedData: {
      totalTime: formatTime(currentJourney.engagement.totalTime),
      activeTime: formatTime(currentJourney.engagement.activeTime),
      idleTime: formatTime(currentJourney.engagement.idleTime),
      focusTime: formatTime(currentJourney.engagement.focusTime)
    }
  }
}

// Format milliseconds to readable time
function formatTime(ms: number): string {
  if (ms < 1000) return '< 1s'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

// Clean up
export function cleanupTracking() {
  if (sectionObserver) {
    sectionObserver.disconnect()
    sectionObserver = null
  }
}